/**
 * FullscreenManager —— 多平台全屏方案
 * 覆盖：微信、iOS Safari、Android Chrome
 */
import { ref } from 'vue';

// ===== 环境检测 =====

const ua = navigator.userAgent;

const isWeixin = /MicroMessenger/i.test(ua);
const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
const isAndroid = /Android/.test(ua);

/** 是否已是全屏 */
function isFullscreen(): boolean {
  return !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).msFullscreenElement
  );
}

/** 显示模式 */
function displayMode(): string {
  if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen';
  if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone';
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui';
  return 'browser';
}

const fullscreenEnabled =
  document.fullscreenEnabled ||
  (document as any).webkitFullscreenEnabled ||
  (document as any).mozFullScreenEnabled ||
  (document as any).msFullscreenEnabled;

// ===== Reactive state for Vue =====

export const isFullscreenActive = ref(false);

// ===== Core: enter / exit =====

async function enterFullscreen(): Promise<boolean> {
  const el = document.documentElement as any;
  try {
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    else if (el.mozRequestFullScreen) await el.mozRequestFullScreen();
    else if (el.msRequestFullscreen) await el.msRequestFullscreen();
    else return false;
    return true;
  } catch {
    return false;
  }
}

async function exitFullscreen(): Promise<boolean> {
  try {
    if (document.exitFullscreen) await document.exitFullscreen();
    else if ((document as any).webkitExitFullscreen) await (document as any).webkitExitFullscreen();
    else if ((document as any).mozCancelFullScreen) await (document as any).mozCancelFullScreen();
    else if ((document as any).msExitFullscreen) await (document as any).msExitFullscreen();
    else return false;
    return true;
  } catch {
    return false;
  }
}

// ===== WeChat handler =====

function setupWeixin() {
  if (!isWeixin) return;

  function hideWeixinToolbar() {
    try {
      (window as any).WeixinJSBridge?.invoke('hideOptionMenu');
    } catch { /* ignore */ }
  }

  if (typeof (window as any).WeixinJSBridge !== 'undefined') {
    hideWeixinToolbar();
  } else {
    document.addEventListener('WeixinJSBridgeReady', hideWeixinToolbar, { once: true });
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      setTimeout(hideWeixinToolbar, 200);
    }
  });
}

// ===== iOS handler =====

function setupIOS() {
  if (!isIOS) return;
  if ((window as any).navigator?.standalone) return;
}

// ===== Android handler =====

function setupAndroid() {
  if (!isAndroid) return;

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && !isFullscreen()) {
      setTimeout(() => enterFullscreen(), 300);
    }
  });
}

// ===== Fullscreen change handler =====

function onFullscreenChange() {
  isFullscreenActive.value = isFullscreen();
  document.body.classList.toggle('fullscreen-active', isFullscreen());
}

// ===== Fallback =====

function fallbackFullscreen() {
  document.body.classList.add('fallback-fullscreen');
  window.scrollTo(0, 1);
}

// ===== Init =====

let initDone = false;

export function initFullscreen() {
  if (initDone) return;
  initDone = true;

  // 日志
  console.groupCollapsed('[Fullscreen] Environment');
  console.log('WeChat:', isWeixin, '| iOS:', isIOS, '| Android:', isAndroid);
  console.log('API enabled:', fullscreenEnabled, '| display:', displayMode());
  console.groupEnd();

  // 分平台初始化
  setupWeixin();
  setupIOS();
  setupAndroid();

  // 监听全屏变化
  document.addEventListener('fullscreenchange', onFullscreenChange);
  document.addEventListener('webkitfullscreenchange', onFullscreenChange);
  document.addEventListener('mozfullscreenchange', onFullscreenChange);
  document.addEventListener('MSFullscreenChange', onFullscreenChange);

  // 首次用户触摸 → 尝试进入全屏
  let fullscreenTried = false;
  async function tryEnter() {
    if (fullscreenTried) return;
    fullscreenTried = true;

    const mode = displayMode();
    if (mode === 'fullscreen' || mode === 'standalone') return;
    if ((window as any).navigator?.standalone) return;

    const ok = await enterFullscreen();
    if (!ok && !isWeixin) fallbackFullscreen();
  }
  document.addEventListener('touchstart', tryEnter, { once: true });
  document.addEventListener('click', tryEnter, { once: true });
}

/** 手动切换全屏 */
export async function toggleFullscreen() {
  if (isFullscreen()) {
    await exitFullscreen();
  } else {
    await enterFullscreen();
  }
}
